import React from 'react';
import connectToStores from 'alt-utils/lib/connectToStores';
import CoachingPlanStore from 'stores/coaching/CoachingPlanStore';
import DirectReportsStore from 'stores/coaching/DirectReportsStore';
import CoachingPlanActions from 'actions/coaching/CoachingPlanActions';
import UserActions from 'actions/UserActions';
import UserStore from 'stores/UserStore';
import ExecCoachingManagerRollup from 'components/dashboard/admin/ExecCoachingManagerRollup';
import TeamListStore from 'stores/TeamListStore';
import TeamActions from 'actions/TeamActions';
import DashV2MainWidgetLoader from 'components/dashboard/DashV2MainWidgetLoader';
import {List, Map} from 'immutable';
import DepartmentFilterStore from 'stores/DepartmentFilterStore';
import CoachingReviewTypeStore, {typesAndCategories} from 'stores/coaching/CoachingReviewTypeStore';
import CoachingReviewTypeSource from 'sources/coaching/CoachingReviewTypeSource';
import {associateBy} from 'utils/associateBy';

@connectToStores
export default class ExecCoachingManagerRollupContainer extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      coaches: List(),
      coachesLoading: true,
      inactiveReviewTypesLoading: true,
      inactiveReviewTypesError: false,
      inactiveReviewTypes: Map(), // Map<CoachingReviewTypeId, CoachingReviewType>
    };
  }

  static getStores() {
    return [
      CoachingPlanStore,
      CoachingReviewTypeStore,
      DirectReportsStore,
      TeamListStore,
      DepartmentFilterStore,
    ];
  }

  static getPropsFromStores(props) {
    const {startDate, endDate} = props;
    const planStore = CoachingPlanStore.getState();

    const plans = planStore.planMap;
    const planMap = plans.filter(p => p.wasActiveDuringRange(startDate, endDate));
    const departmentIds = DepartmentFilterStore.getState().departmentSubset;

    const {lastCompletePlan, overduePlans, areCoachingPlansLoading} = planStore;

    const teams = TeamListStore.getState().teams.valueSeq();
    const teamManagerIds = teams.map(team => team.managerId).toSet();
    // Any users with coaching plans the user can view
    const users = UserStore.getState()
      .users.toList()
      .filter(user => (!departmentIds.isEmpty() ? departmentIds.has(user.departmentId) : true))
      .filter(u => u.isManager() || u.is_coach);

    const [typesLoaded, types] = typesAndCategories(CoachingReviewTypeStore);
    const {meetingCategories, meetingCategoriesLoaded} = CoachingReviewTypeStore.getState();
    return {
      users,
      teamManagerIds,
      planMap,
      lastCompletePlan,
      overduePlans,
      areCoachingPlansLoading,
      departmentIds,
      typesLoaded,
      types,
      meetingCategories, // We need these to add to the inactive review types.
      meetingCategoriesLoaded,
    };
  }

  componentDidMount() {
    const {departmentIds} = this.props;
    this.setState({coachesLoading: true});

    // appeasing the alt gods
    setTimeout(async () => {
      try {
        TeamActions.fetchTeamsForUser();
        const managers = await UserActions.fetchAll({
          roles: ['manager', 'admin'],
        });
        const coaches = await UserActions.fetchAll({isCoach: true});
        const coachesAndManagers = managers
          .concat(coaches)
          .toSet()
          .filter(user => (!departmentIds.isEmpty() ? departmentIds.has(user.departmentId) : true))
          .toList();

        this.setState({coaches: coachesAndManagers, coachesLoading: false}, () => this.fetchData());
      } catch (e) {
        this.setState({coachesLoading: false});
      }
    }, 0);
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    const {startDate, endDate} = this.props;

    // Shouldn't happen since these are always provided to each dash widget
    if (!startDate || !endDate) {
      return;
    }

    if (!startDate.isSame(prevProps.startDate) || !endDate.isSame(prevProps.endDate)) {
      this.fetchData();
    }
  }

  fetchData() {
    const {startDate, endDate, types, meetingCategories} = this.props;
    const coachIds = this.state.coaches.map(u => u.id);
    setTimeout(() => {
      CoachingPlanActions.fetchLastCompleteAndOverdue(coachIds, endDate);
      CoachingPlanActions.fetchForCoaches(coachIds, {
        includeReviews: true,
        start: startDate,
        end: endDate,
      }).then(plans => {
        const planReviewTypeIds = plans
          .map(plan =>
            plan.reviews.map(review => review.reviewTypeId).filter(reviewTypeId => reviewTypeId)
          )
          .flatten()
          .toSet();
        // We only fetch active review types, but some reviews may also have inactive types, and we
        // still need to report on them. Get the ids of reviews that we don't have yet so we can
        // fetch them and store them in state.
        const storedReviewTypeIds = types.map(type => type.id).toSet();
        const missingReviewTypeIds = planReviewTypeIds.subtract(storedReviewTypeIds);
        if (missingReviewTypeIds.isEmpty()) {
          this.setState({
            inactiveReviewTypesLoading: false,
          });
        } else {
          this.setState({inactiveReviewTypesLoading: true}, () => {
            CoachingReviewTypeSource.fetchByIds(missingReviewTypeIds.toJS()).then(
              reviewTypes => {
                const inactiveReviewTypes = associateBy(
                  List(reviewTypes).map(reviewType =>
                    // Need this to avoid gray boxes.
                    reviewType.set(
                      'meetingCategory',
                      meetingCategories.get(reviewType.meetingCategoryId)
                    )
                  ),
                  rt => rt.id
                );
                this.setState({
                  inactiveReviewTypes,
                  inactiveReviewTypesLoading: false,
                });
              },
              error => {
                // Probably no sense in bothering the user with this, since we don't have error
                // handling for the active types here, either.
                console.error('Error loading review types', error);
                this.setState({
                  inactiveReviewTypes: Map(),
                  inactiveReviewTypesLoading: false,
                });
              }
            );
          });
        }
      });
    }, 0);
  }

  render() {
    const {typesLoaded, types} = this.props;
    if (
      this.state.coachesLoading ||
      this.props.areCoachingPlansLoading ||
      this.state.inactiveReviewTypesLoading ||
      !typesLoaded
    ) {
      return <DashV2MainWidgetLoader />;
    }

    // Merge inactive and active review types for reporting.
    const allReviewTypes = types.merge(this.state.inactiveReviewTypes);

    return <ExecCoachingManagerRollup {...this.props} reviewTypes={allReviewTypes} />;
  }
}
